import React from "react";
import { getRedZoneFill, getZoneFill, getZoneStroke, RED_ZONE_STROKE } from "../../../utils/map/zone-helper";

export interface ZoneSvgPolygonProps extends React.SVGProps<SVGPolygonElement> {
  /** Whether the zone is currently selected. */
  isSelected: boolean;
  /** Whether the zone is a Teach-and-Repeat zone (blue family). Defaults to false (= cleaning zone). */
  isTnR?: boolean;
  /** Whether the zone is a restricted (red) zone. */
  isRedZone?: boolean;
  /**
   * Whether the zone is highlighted from an external source (e.g. hovering an entry in a list).
   * Combined with internal mouse-hover state to compute the final colour.
   */
  externalHovered?: boolean;
  /** Called when the pointer enters or leaves the polygon. */
  onHoverChange?: (hovered: boolean) => void;
}

/**
 * A base SVG `<polygon>` element with zone-type-aware fill/stroke colours.
 *
 * Handles both internal (direct mouse hover) and external hover state and
 * computes the correct fill colour. Hover always inverts the current state:
 * hovering an unselected zone shows the selected colour, and hovering a
 * selected zone shows the unselected (base) colour.
 *
 * Remaining `React.SVGProps<SVGPolygonElement>` props (points, strokeWidth,
 * onClick, data-* attributes, …) are forwarded to the underlying `<polygon>`.
 *
 * The component uses `React.forwardRef` so it can be used as a direct child
 * of MUI `<Tooltip>` (which needs to attach a ref to its child).
 *
 * ### Fill / stroke overrides
 * Passing an explicit `fill` prop (e.g. `fill="url(#stripes)"` for evacuation
 * zones) takes precedence over the computed fill. Passing `stroke` overrides
 * the computed stroke.
 */
const ZoneSvgPolygon = React.forwardRef<SVGPolygonElement, ZoneSvgPolygonProps>(
  (
    {
      isTnR = false,
      isRedZone = false,
      isSelected,
      externalHovered = false,
      onHoverChange,
      // Explicit overrides — keep out of `rest` so they don't clash with computed values
      fill: fillProp,
      stroke: strokeProp,
      fillOpacity = 0.5,
      strokeOpacity = 0.8,
      style,
      onMouseEnter,
      onMouseLeave,
      ...rest
    },
    ref,
  ) => {
    const [internalHovered, setInternalHovered] = React.useState(false);
    const isHovered = internalHovered || externalHovered;

    let computedFill: string;
    let computedStroke: string;
    if (isRedZone) {
      computedFill = getRedZoneFill(isSelected, isHovered);
      computedStroke = RED_ZONE_STROKE;
    } else {
      computedFill = getZoneFill(isTnR, isSelected, isHovered);
      computedStroke = getZoneStroke(isTnR);
    }

    const handleMouseEnter = (e: React.MouseEvent<SVGPolygonElement>) => {
      setInternalHovered(true);
      onHoverChange?.(true);
      onMouseEnter?.(e);
    };

    const handleMouseLeave = (e: React.MouseEvent<SVGPolygonElement>) => {
      setInternalHovered(false);
      onHoverChange?.(false);
      onMouseLeave?.(e);
    };

    return (
      <polygon
        ref={ref}
        fill={fillProp ?? computedFill}
        stroke={strokeProp ?? computedStroke}
        fillOpacity={fillOpacity}
        strokeOpacity={strokeOpacity}
        style={{ pointerEvents: "visible", ...style }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        {...rest}
      />
    );
  },
);

ZoneSvgPolygon.displayName = "ZoneSvgPolygon";

export default ZoneSvgPolygon;
